import { Checkbox, Divider, Input, Switch } from 'antd';
import { memo, useCallback, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { ICheckoutItem } from '../../../interfaces/ICheckout';
import { resolveAssetUrl } from '../../../utils/resolveAssetUrl';
import { useForm } from '../context';
import { moneyMask } from '../helper';

const MAX_CENTS = 99999999;

type OrderBumpItemPayload = ICheckoutItem & {
  valueType?: string;
  openValueMinAmount?: number | string;
  openValueMaxAmount?: number | string;
};

function isOpenValueBump(item: OrderBumpItemPayload): boolean {
  return !!item.config?.isOpenValue || item.valueType === 'open';
}

function getOpenValueMinMax(item: OrderBumpItemPayload): { min?: number; max?: number } {
  const toCents = (v: unknown): number | undefined =>
    v != null && typeof v === 'number' && !Number.isNaN(v) ? v : undefined;
  return {
    min: item.config?.openValueMinAmount ?? toCents(item.openValueMinAmount),
    max: item.config?.openValueMaxAmount ?? toCents(item.openValueMaxAmount),
  };
}

const OrderBump = memo(() => {
  const { checkout, items, addItems, removeItems, updateItemAmount } = useForm();

  const [selectedBump, setSelectedBump] = useState<ICheckoutItem[]>([]);
  const [openValueDisplays, setOpenValueDisplays] = useState<Record<string, string>>({});
  const [openValueErrors, setOpenValueErrors] = useState<Record<string, string>>({});

  const bumpItems = checkout?.items?.filter((item) => item.type == 'order_bump') ?? [];

  const applyCentsForItem = useCallback(
    (item: ICheckoutItem, amountInCents: number, input?: HTMLInputElement) => {
      const { min: minAmount, max: maxAmount } = getOpenValueMinMax(item);
      const capped = Math.max(0, Math.min(amountInCents, MAX_CENTS));
      const cappedByMax = maxAmount != null ? Math.min(capped, maxAmount) : capped;
      const formatted = moneyMask.apply(cappedByMax / 100);
      setOpenValueDisplays((prev) => ({ ...prev, [item.id]: formatted }));

      if (cappedByMax <= 0) {
        setOpenValueErrors((prev) => ({ ...prev, [item.id]: 'Informe um valor válido' }));
        updateItemAmount(item.id, 0);
      } else if (minAmount != null && cappedByMax < minAmount) {
        setOpenValueErrors((prev) => ({
          ...prev,
          [item.id]: `Valor mínimo: R$ ${moneyMask.apply(minAmount / 100)}`,
        }));
        updateItemAmount(item.id, 0);
      } else if (maxAmount != null && cappedByMax > maxAmount) {
        setOpenValueErrors((prev) => ({
          ...prev,
          [item.id]: `Valor máximo: R$ ${moneyMask.apply(maxAmount / 100)}`,
        }));
        updateItemAmount(item.id, 0);
      } else {
        setOpenValueErrors((prev) => {
          const next = { ...prev };
          delete next[item.id];
          return next;
        });
        updateItemAmount(item.id, cappedByMax);
      }
      requestAnimationFrame(() => {
        if (input) input.setSelectionRange(formatted.length, formatted.length);
      });
    },
    [updateItemAmount]
  );

  const handleOpenValueKeyDown = useCallback(
    (item: ICheckoutItem) =>
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        e.stopPropagation();
        const input = (e.target as HTMLInputElement)?.tagName === 'INPUT'
          ? (e.target as HTMLInputElement)
          : (e.target as HTMLElement).querySelector?.('input') as HTMLInputElement | null;
        if (!input) return;
        const currentCents = Math.round(moneyMask.clean(input.value || '0') * 100);
        if (e.key >= '0' && e.key <= '9') {
          e.preventDefault();
          const digit = parseInt(e.key, 10);
          applyCentsForItem(item, currentCents * 10 + digit, input);
          return;
        }
        if (e.key === 'Backspace') {
          e.preventDefault();
          applyCentsForItem(item, Math.floor(currentCents / 10), input);
        }
      },
    [applyCentsForItem]
  );

  const handleOpenValuePaste = useCallback(
    (item: ICheckoutItem) => (e: React.ClipboardEvent<HTMLInputElement>) => {
      e.preventDefault();
      const raw = (e.clipboardData.getData('text') || '').replace(/[^\d,]/g, '');
      const amountInCents = Math.round((moneyMask.clean(raw) || 0) * 100);
      applyCentsForItem(item, amountInCents, e.currentTarget);
    },
    [applyCentsForItem]
  );

  const handleOpenValueChange = useCallback(
    (item: ICheckoutItem) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value.replace(/[^\d,]/g, '');
      const amountInCents = Math.round((moneyMask.clean(raw) || 0) * 100);
      applyCentsForItem(item, amountInCents, e.target);
    },
    [applyCentsForItem]
  );

  if (!bumpItems.length) {
    return null;
  }

  const itemsToAdd = bumpItems.map((item) => {
    if (!isOpenValueBump(item)) return item;
    const { min, max } = getOpenValueMinMax(item);
    return {
      ...item,
      amount: 0,
      config: item.config ?? {
        isOpenValue: true,
        openValueMinAmount: min,
        openValueMaxAmount: max,
      },
    };
  });

  function toggleItems(checked: boolean) {
    if (checked) {
      addItems(itemsToAdd);
      setSelectedBump(bumpItems);
      return;
    }

    removeItems(bumpItems);
    setSelectedBump([]);
    setOpenValueDisplays({});
    setOpenValueErrors({});
  }

  function handleAddItem(item: ICheckoutItem) {
    const itemToAdd = isOpenValueBump(item)
      ? (() => {
          const { min, max } = getOpenValueMinMax(item);
          return {
            ...item,
            amount: 0,
            config: item.config ?? {
              isOpenValue: true,
              openValueMinAmount: min,
              openValueMaxAmount: max,
            },
          };
        })()
      : item;
    addItems([itemToAdd]);
    setSelectedBump((prev) => Array.from(new Set([...prev, item])));
  }

  function handleRemoveItem(item: ICheckoutItem) {
    removeItems([item]);

    setSelectedBump((prev) => {
      return prev.filter((i) => i.id !== item.id);
    });

    if (isOpenValueBump(item)) {
      setOpenValueDisplays((prev) => {
        const next = { ...prev };
        delete next[item.id];
        return next;
      });
      setOpenValueErrors((prev) => {
        const next = { ...prev };
        delete next[item.id];
        return next;
      });
    }
  }

  const orderBumpTitle = checkout?.orderBumpTitle;
  const orderBumpSubtitle = checkout?.orderBumpSubtitle;
  const isAllItemsSelected = selectedBump.length === bumpItems.length;

  return (
    <>
      <Divider variant="dashed" />

      <div className="mt-4 rounded-lg bg-gray-100 p-6">
        <div className="flex flex-col gap-1">
          <p className="text-black font-medium leading-tight">{orderBumpTitle}</p>
          <p className="text-gray-600">{orderBumpSubtitle}</p>
        </div>

        <div className="mt-4 flex items-center gap-2">
          <Switch id="order-bump-switch" checked={isAllItemsSelected} onChange={toggleItems} />

          <label htmlFor="order-bump-switch" className="text-gray-600 cursor-pointer">
            Adicionar todas as ofertas
          </label>
        </div>

        <div className="grid grid-cols-1 gap-2 mt-4">
          {bumpItems.map((item) => {
            // Casa por `id` (único e sempre presente), não por `code` — o `code` é opcional e vem
            // null p/ muitos itens, e `null == null` dava falso-positivo contra o produto principal
            // (bump aparecia marcado por padrão, sem entrar no total, e não desmarcava).
            const isAdded = items.some((i) => i.id == item.id);
            const addedItem = items.find((i) => i.id == item.id);
            const totalDiscount = addedItem ? addedItem.amount - (addedItem.couponItem?.amount ?? addedItem.amount) : 0;

            return (
              <div
                onClick={() => {
                  if (!isAdded) {
                    handleAddItem(item);
                  }
                }}
                className={twMerge(
                  'w-full flex gap-4 items-center p-2 rounded-lg cursor-pointer transition-all duration-300 hover:bg-emerald-50',
                  isAdded && 'bg-emerald-50 cursor-default'
                )}
              >
                <Checkbox
                  checked={isAdded}
                  // O checkbox é a autoridade do marcar/desmarcar; impede que o clique também
                  // dispare o `onClick` do row (que só adiciona) e cause re-add ao desmarcar.
                  onClick={(e) => e.stopPropagation()}
                  onChange={(event) => {
                    if (event.target.checked) {
                      handleAddItem(item);
                      return;
                    }

                    handleRemoveItem(item);
                  }}
                />

                <img
                  src={resolveAssetUrl(item.image)}
                  alt={item.description}
                  className="w-16 h-16 sm:w-20 sm:h-20 object-center object-cover rounded-lg"
                />

                <div className="flex flex-col justify-start flex-1 min-w-0">
                  <p className="text-black font-medium leading-tight">{item.description}</p>

                  <p className="text-gray-600 leading-tight hidden sm:block">{item.details?.slice(0, 100)}</p>

                  <div className="text-black font-medium mt-1" onClick={(e) => e.stopPropagation()}>
                    {isOpenValueBump(item) && isAdded ? (
                      <div className="flex flex-col gap-1 max-w-48">
                        <label className="text-sm text-gray-600">Informe o valor:</label>
                        <Input
                          prefix="R$"
                          size="small"
                          placeholder="0,00"
                          value={
                            openValueDisplays[item.id] ??
                            (addedItem && addedItem.amount > 0 ? moneyMask.apply(addedItem.amount / 100) : '')
                          }
                          onChange={handleOpenValueChange(item)}
                          onKeyDown={handleOpenValueKeyDown(item)}
                          onPaste={handleOpenValuePaste(item)}
                          status={openValueErrors[item.id] ? 'error' : undefined}
                        />
                        {openValueErrors[item.id] && (
                          <p className="text-red-500 text-xs">{openValueErrors[item.id]}</p>
                        )}
                        {!openValueErrors[item.id] && getOpenValueMinMax(item).min != null && (
                          <p className="text-gray-400 text-xs">
                            Mínimo: R$ {moneyMask.apply(getOpenValueMinMax(item).min! / 100)}
                          </p>
                        )}
                      </div>
                    ) : isOpenValueBump(item) && !isAdded ? (
                      <span className="text-gray-500">Valor a definir ao adicionar</span>
                    ) : totalDiscount === 0 ? (
                      new Intl.NumberFormat('pt-BR', {
                        style: 'currency',
                        currency: 'BRL',
                      }).format((addedItem?.amount ?? item.amount) / 100)
                    ) : (
                      <>
                        <s>
                          {new Intl.NumberFormat('pt-BR', {
                            style: 'currency',
                            currency: 'BRL',
                          }).format(item.amount / 100)}
                        </s>{' '}
                        {new Intl.NumberFormat('pt-BR', {
                          style: 'currency',
                          currency: 'BRL',
                        }).format((item.amount - totalDiscount) / 100)}
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
});

export default OrderBump;
